<template>
  <div class="pdf-viewer">
    <div class="toolbar">
      <div class="toolbar-actions">
        <Button type="primary" @click="goBack">Back</Button>
        <Button v-if="resolvedDataUrl || resolvedUrl" type="primary-light" @click="printPdf">Print</Button>
        <Button v-if="resolvedDataUrl || resolvedUrl" type="light" @click="openInNewTab">Open in new tab</Button>
      </div>
      <div class="session-control">
        <label class="session-checkbox">
          <input
            type="checkbox"
            v-model="keepSessionData"
            @change="updateSessionPreference"
          />
          <span>Keep PDF data during session</span>
        </label>
      </div>
    </div>
    <div class="frame-wrap">
      <div v-if="loadError" class="error">{{ loadError }}</div>
      <div v-else-if="pdfSrc" class="frame">
        <pdf
          v-for="pageNum in numPages"
          :key="pageNum"
          :src="pdfSrc"
          :page="pageNum"
          class="pdf-page"
          @error="onPdfError"
        ></pdf>
      </div>
      <div v-else class="error">No PDF to display.</div>
    </div>
  </div>
</template>

<script>
import Button from '@/components/forms/Button.vue';
import pdf from 'vue-pdf';

export default {
  name: 'PdfViewer',
  components: {
    Button,
    pdf
  },
  data: () => ({
    resolvedUrl: '',
    resolvedDataUrl: '',
    pdfSrc: null,
    numPages: 0,
    loadError: '',
    keepSessionData: true // Default to keeping data
  }),
  methods: {
    goBack() {
      // Navigate back to the source route where user came from
      const sourceRoute = sessionStorage.getItem('pdfViewer_sourceRoute') || '/';

      // Clear PDF data if user preference is to not keep session data
      if (!this.keepSessionData) {
        this.clearSessionData();
      }

      // Clear the source route from session storage
      sessionStorage.removeItem('pdfViewer_sourceRoute');

      this.$router.push(sourceRoute);
    },
    updateSessionPreference() {
      // Save preference to localStorage for persistence across sessions
      localStorage.setItem('pdfViewer_keepSessionData', this.keepSessionData.toString());

      // If user unchecks, clear current session data
      if (!this.keepSessionData) {
        this.clearSessionData();
      }
    },
    clearSessionData() {
      sessionStorage.removeItem('pdfBase64');
      sessionStorage.removeItem('pdfUrl');
      this.resolvedUrl = '';
      this.resolvedDataUrl = '';
      this.pdfSrc = null;
      this.numPages = 0;
      this._loadedSrc = null;
    },
    loadSessionPreference() {
      // Load preference from localStorage, default to true
      const saved = localStorage.getItem('pdfViewer_keepSessionData');
      this.keepSessionData = saved !== null ? saved === 'true' : true;
    },
    refreshFromStorage() {
      try {
        const b64 = sessionStorage.getItem('pdfBase64');
        const url = sessionStorage.getItem('pdfUrl');
        if (b64 && b64.length > 0) {
          this.resolvedDataUrl = `data:application/pdf;base64,${b64}`;
          this.resolvedUrl = '';
          this.loadPdf(this.resolvedDataUrl);
        } else if (url && url.length > 0) {
          this.resolvedUrl = url;
          this.resolvedDataUrl = '';
          this.loadPdf(this.resolvedUrl);
        }
      } catch (_) {}
    },
    loadPdf(src) {
      // created() and the 'pdf-update' listener can both fire for the same navigation
      // (the latter exists to refresh an already-mounted viewer); skip redundant reloads
      // of the same source so we don't race two loadingTasks against the shared pdf.js worker.
      if (src === this._loadedSrc) return;
      this._loadedSrc = src;

      this.loadError = '';
      this.numPages = 0;
      const task = pdf.createLoadingTask(src);
      this.pdfSrc = task;
      task.promise.then(doc => {
        if (this.pdfSrc !== task) return; // superseded by a newer load
        this.numPages = doc.numPages;
      }).catch(err => {
        if (this.pdfSrc !== task) return;
        this.loadError = 'PDF-filen kunde inte laddas (' + (err && err.message ? err.message : 'okänt fel') + ')';
      });
    },
    onPdfError(err) {
      this.loadError = 'PDF-filen kunde inte visas (' + (err && err.message ? err.message : 'okänt fel') + ')';
    },
    dataUrlToBlobUrl(dataUrl) {
      const base64 = dataUrl.split(',')[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    },
    getViewableUrl() {
      if (this.resolvedDataUrl) return this.dataUrlToBlobUrl(this.resolvedDataUrl);
      return this.resolvedUrl || '';
    },
    openInNewTab() {
      // Chrome blocks window.open() on a data: URI outright (shows about:blank#blocked),
      // so resolvedDataUrl always goes through getViewableUrl()'s blob: URL conversion.
      const target = this.getViewableUrl();
      if (!target) return;
      window.open(target, '_blank');
    },
    printPdf() {
      // pdf.js's own canvas print renders some embedded TrueType fonts as boxes instead of
      // glyphs (a pdf.js rasterizer limitation, confirmed independent of pdfjs-dist version).
      // Printing the actual PDF through a hidden iframe uses the browser's native PDF engine
      // instead, which doesn't have that bug.
      const target = this.getViewableUrl();
      if (!target) return;

      const printFrame = document.createElement('iframe');
      printFrame.style.cssText = 'position: fixed; right: 0; bottom: 0; width: 0; height: 0; border: 0;';

      const cleanup = () => {
        if (printFrame.parentNode) printFrame.parentNode.removeChild(printFrame);
      };

      printFrame.onload = () => {
        // The iframe's "load" event fires once the browser's internal PDF viewer shell is in
        // place, not once it has actually parsed and rendered the document - calling print()
        // immediately here prints a blank page. Give it a moment to finish rendering first.
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (e) {
            this.loadError = 'Utskrift misslyckades (' + (e && e.message ? e.message : 'okänt fel') + ')';
            cleanup();
            return;
          }
          printFrame.contentWindow.addEventListener('afterprint', cleanup, { once: true });
          setTimeout(cleanup, 60000); // fallback if afterprint never fires
        }, 500);
      };

      document.body.appendChild(printFrame);
      printFrame.src = target;
    }
  },
  created() {
    try {
      // Prefer Base64 from sessionStorage
      this.refreshFromStorage();
      if (!this.resolvedDataUrl && !this.resolvedUrl) {
        const raw = this.$route.query.url || '';
        if (!raw || typeof raw !== 'string') return;
        new URL(raw); // validates raw is a well-formed URL
        this.resolvedUrl = raw;
        this.loadPdf(this.resolvedUrl);
      }
    } catch (e) {
      this.resolvedUrl = '';
    }
  },
  mounted() {
    // Load session preference
    this.loadSessionPreference();

    // Listen for updates from the app
    this._onPdfUpdate = () => this.refreshFromStorage();
    window.addEventListener('pdf-update', this._onPdfUpdate);
  },
  beforeDestroy() {
    if (this._onPdfUpdate) window.removeEventListener('pdf-update', this._onPdfUpdate);
  }
}
</script>

<style scoped>
.pdf-viewer { display: flex; flex-direction: column; height: 100%; padding: 12px; }
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.session-control {
  display: flex;
  align-items: center;
}
.session-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #e2e8f0;
  font-size: 14px;
  user-select: none;
}
.session-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #f59100;
  cursor: pointer;
}
.session-checkbox span {
  color: #e2e8f0;
}
.frame-wrap { flex: 1; min-height: 0; }
.frame { width: 100%; height: calc(100vh - 70px); background: #101820; overflow-y: auto; padding: 12px 0; }
.pdf-page { display: block; max-width: 900px; margin: 0 auto 12px; box-shadow: 0 0 8px rgba(0, 0, 0, 0.4); }
.error { color: #fca5a5; padding: 16px; }
</style>
